import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import s from '../../pages/Financeiro.module.css';

// Conciliação bancária com o Cora — compara o extrato real da conta com o
// que já está lançado aqui, e mostra só o que falta (ex.: "Taxa boleto
// cancelado via API", R$0,30, que o Cora cobra sozinho e não tem rastro
// nenhum até essa tela existir). Ver backend/src/controllers/
// conciliacaoCoraController.js.
export default function ModalConciliacaoCora({ contaId, contaNome, dataInicio, dataFim, onClose, onConciliado }) {
  const [itens, setItens] = useState(null);
  const [erro, setErro] = useState('');
  const [processando, setProcessando] = useState(null); // cora_transacao_id em ação
  const [expandido, setExpandido] = useState(null); // cora_transacao_id com o mini-form de "gerar conta" aberto
  const [formConta, setFormConta] = useState({ descricao: '', plano_conta_id: '', vincularCadastro: true });
  const [planoContasReceita, setPlanoContasReceita] = useState([]);
  const [planoContasDespesa, setPlanoContasDespesa] = useState([]);

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const { data } = await api.get(`/financeiro/contas/${contaId}/conciliar-cora`, {
        params: { data_inicio: dataInicio, data_fim: dataFim },
      });
      setItens(data);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao buscar o extrato do Cora.');
      setItens([]);
    }
  }, [contaId, dataInicio, dataFim]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/financeiro/plano-contas/analiticas', { params: { tipo: 'receita' } })
      .then(({ data }) => setPlanoContasReceita(data)).catch(() => {});
    api.get('/financeiro/plano-contas/analiticas', { params: { tipo: 'despesa,custo' } })
      .then(({ data }) => setPlanoContasDespesa(data)).catch(() => {});
  }, []);

  const remover = (id) => {
    setItens(prev => prev.filter(i => i.cora_transacao_id !== id));
    onConciliado?.();
  };

  const lancarDireto = async (item) => {
    setProcessando(item.cora_transacao_id);
    try {
      await api.post(`/financeiro/contas/${contaId}/conciliar-cora/lancar`, {
        cora_transacao_id: item.cora_transacao_id,
        tipo: item.tipo,
        valor: item.valor,
        descricao: item.descricao,
        data: item.data,
        // Taxas conhecidas do Cora (compensado/cancelado) já caem sozinhas
        // na categoria certa do plano de contas — ver plano_conta_sugerido_id
        // no backend (conciliacaoCoraController#pendentes).
        plano_conta_id: item.plano_conta_sugerido_id || null,
      });
      remover(item.cora_transacao_id);
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao lançar.');
    } finally {
      setProcessando(null);
    }
  };

  const abrirGerarConta = (item) => {
    setExpandido(item.cora_transacao_id);
    // Já vem marcado pra vincular/cadastrar de cara — a maioria das vezes é
    // isso que se quer; a usuária desmarca se não quiser dessa vez. Categoria
    // já vem pré-selecionada quando é uma das taxas conhecidas do Cora.
    setFormConta({
      descricao: item.descricao,
      plano_conta_id: item.plano_conta_sugerido_id ? String(item.plano_conta_sugerido_id) : '',
      vincularCadastro: true,
    });
  };

  const confirmarGerarConta = async (item) => {
    setProcessando(item.cora_transacao_id);
    try {
      const usarVinculo = formConta.vincularCadastro && item.contraparte;
      await api.post(`/financeiro/contas/${contaId}/conciliar-cora/gerar-conta`, {
        cora_transacao_id: item.cora_transacao_id,
        tipoConta: item.tipo === 'entrada' ? 'receber' : 'pagar',
        valor: item.valor,
        descricao: formConta.descricao || item.descricao,
        data: item.data,
        plano_conta_id: formConta.plano_conta_id || null,
        cliente_id: item.tipo === 'entrada' && usarVinculo ? (item.cadastro_encontrado?.id || null) : null,
        fornecedor_id: item.tipo === 'saida' && usarVinculo ? (item.cadastro_encontrado?.id || null) : null,
        criar_cadastro: usarVinculo && !item.cadastro_encontrado,
        contraparte_nome: item.contraparte,
        contraparte_documento: item.contraparte_documento,
      });
      setExpandido(null);
      remover(item.cora_transacao_id);
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao gerar a conta.');
    } finally {
      setProcessando(null);
    }
  };

  return (
    <div className={s.overlay}>
      <div className={s.modal} style={{ width: 640 }}>
        <div className={s.modalTitle}>🔄 Conciliar com Cora — {contaNome}</div>
        <div className={s.modalBody}>
          <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
            Período: {dataInicio} até {dataFim}. Mostrando só lançamentos que já existem no extrato
            real do Cora e ainda não foram registrados aqui.
          </p>

          {erro && <div className={s.erroMsg}>{erro}</div>}
          {itens === null && <div style={{ fontSize: 13, color: '#888' }}>Buscando extrato do Cora...</div>}
          {itens?.length === 0 && !erro && (
            <div style={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
              ✔ Tudo conciliado — nada pendente nesse período.
            </div>
          )}

          {itens?.map(item => (
            <div key={item.cora_transacao_id} style={{ border: '1px solid var(--border,#ddd)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{item.descricao}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>
                    {item.data.split('-').reverse().join('/')}
                    {item.contraparte ? ` · ${item.contraparte}` : ''}
                  </div>
                </div>
                <div style={{ fontWeight: 700, color: item.tipo === 'entrada' ? '#16a34a' : '#dc2626' }}>
                  {item.tipo === 'entrada' ? '+' : '−'} R$ {item.valor.toFixed(2).replace('.', ',')}
                </div>
              </div>

              {expandido !== item.cora_transacao_id ? (
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <button className={s.btnErp} disabled={processando === item.cora_transacao_id}
                    onClick={() => lancarDireto(item)}>
                    Só lançar como movimentação
                  </button>
                  <button className={s.btnErp} disabled={processando === item.cora_transacao_id}
                    onClick={() => abrirGerarConta(item)}>
                    Gerar Conta a {item.tipo === 'entrada' ? 'Receber' : 'Pagar'}
                  </button>
                </div>
              ) : (
                <div style={{ marginTop: 10, borderTop: '1px solid var(--border,#eee)', paddingTop: 10 }}>
                  <div className={s.formGrupo} style={{ marginBottom: 8 }}>
                    <label className={s.label}>Descrição</label>
                    <input className={`${s.input} ${s.inputLarge}`}
                      value={formConta.descricao}
                      onChange={e => setFormConta(f => ({ ...f, descricao: e.target.value }))} />
                  </div>
                  <div className={s.formGrupo} style={{ marginBottom: 8 }}>
                    <label className={s.label}>Categoria (Plano de Contas)</label>
                    <select className={`${s.select} ${s.inputLarge}`}
                      value={formConta.plano_conta_id}
                      onChange={e => setFormConta(f => ({ ...f, plano_conta_id: e.target.value }))}>
                      <option value="">— sem categoria —</option>
                      {(item.tipo === 'entrada' ? planoContasReceita : planoContasDespesa).map(pc => (
                        <option key={pc.id} value={pc.id}>{pc.codigo} — {pc.descricao}</option>
                      ))}
                    </select>
                  </div>

                  {item.contraparte && (
                    <div style={{ marginBottom: 10, padding: '8px 10px', background: 'var(--content-bg,#f8f9fa)', borderRadius: 6, fontSize: 12.5 }}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={formConta.vincularCadastro}
                          onChange={e => setFormConta(f => ({ ...f, vincularCadastro: e.target.checked }))}
                          style={{ marginTop: 2 }} />
                        {item.cadastro_encontrado ? (
                          <span>
                            Vincular ao {item.tipo === 'entrada' ? 'cliente' : 'fornecedor'} já cadastrado:{' '}
                            <strong>{item.cadastro_encontrado.nome}</strong>
                          </span>
                        ) : (
                          <span>
                            Cadastrar <strong>{item.contraparte}</strong>
                            {item.contraparte_documento ? ` (${item.contraparte_documento})` : ''} como novo{' '}
                            {item.tipo === 'entrada' ? 'cliente' : 'fornecedor'} e vincular
                          </span>
                        )}
                      </label>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className={s.btnErpPrimario} disabled={processando === item.cora_transacao_id}
                      onClick={() => confirmarGerarConta(item)}>
                      {processando === item.cora_transacao_id ? 'Salvando...' : 'Confirmar'}
                    </button>
                    <button className={s.btnErp} onClick={() => setExpandido(null)}>Cancelar</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}
